/**
 * Read-only decision support for one VulnerabilityEvent.
 *
 * The same blocks are shown in the expanded table row (where an auditor must be
 * able to read everything, audit trail included) and inside the Bewertung dialog
 * (where they are the basis for the verdict). Keeping them in one place is what
 * guarantees the dialog can never show *less* than the row it was opened from.
 *
 * Every timestamp here is a raw `CmdbDateTime` — rendered through `fmtDate` /
 * `fmtDay`, never through `new Date(...)`.
 */

import { Trans, useLingui } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import CveLink from '../../components/CveLink';
import Chip, { ChipList } from '../../components/Chip';
import DataTable, { type DataTableColumn } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import { DASH, fmtDate, fmtDay, fmtScore, fmtValue } from '../../lib/format';
import type {
  BetroffeneInstanz,
  VulnerabilityEventRow,
} from '../../lib/types';

import {
  BEWERTUNG_LABELS,
  BEWERTUNG_TONE,
  isNotificationSent,
  kundenOf,
  NOTIFICATION_STATUS_LABELS,
  notificationTone,
  parseScore,
  produktVersionLabel,
  scoreTone,
} from './eventHelpers';

interface FactProps {
  label: string;
  children: ReactNode;
}

/** One `<dt>/<dd>` pair; renders an em dash instead of collapsing empty rows. */
function Fact({ label, children }: FactProps): ReactNode {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export interface EventFactsProps {
  event: VulnerabilityEventRow;
}

/** CVE master data + Dependency-Track's native KEV assertion facts. */
export function EventCveFacts({ event }: EventFactsProps): ReactNode {
  const score = parseScore(event.cve.base_score);
  const kev = event.kev;
  const kevKnown =
    kev.name !== null ||
    kev.beschreibung !== null ||
    kev.aufgenommen_am !== null ||
    kev.empfohlene_massnahme !== null ||
    kev.sources.length > 0;
  const { t } = useLingui();
  const cvssVersion = event.cve.cvss_version;

  return (
    <div className="triage-section">
      <h3><Trans>Schwachstelle</Trans></h3>
      <dl className="detail-list">
        <Fact label="CVE">
          <ChipList>
            <CveLink cve={event.cve.id} />
            {score === null ? null : (
              <Chip tone={scoreTone(score)}>
                CVSS {fmtScore(score)}
              </Chip>
            )}
            {event.cve.aktiv_ausgenutzt ? (
              <Chip tone="alert">
                <Trans>aktiv ausgenutzt</Trans>
              </Chip>
            ) : null}
          </ChipList>
        </Fact>
        <Fact label={t`Beschreibung`}>
          <span className={event.cve.beschreibung ? undefined : 'muted'}>
            {fmtValue(event.cve.beschreibung)}
          </span>
        </Fact>
        <Fact label={t`CVSS-Vektor`}>
          {event.cve.cvss_vector ? (
            <span className="mono">{event.cve.cvss_vector}</span>
          ) : (
            <span className="muted">{cvssVersion ? t`CVSS ${cvssVersion}, ohne Vektor` : DASH}</span>
          )}
        </Fact>
        <Fact label={t`CVE veröffentlicht`}>{fmtDay(event.cve.published_at)}</Fact>
        <Fact label={t`EUVD-ID`}>
          {event.cve.euvd_id && event.cve.euvd_url ? (
            <a className="mono" href={event.cve.euvd_url} target="_blank" rel="noopener noreferrer"
              title={t`Eintrag in der ENISA EU Vulnerability Database öffnen`}>
              {event.cve.euvd_id} <Icon name="external" size={12} />
            </a>
          ) : (
            <span className="muted"><Trans>noch nicht vergeben</Trans></span>
          )}
        </Fact>
        {kevKnown ? (
          <>
            <Fact label={t`KEV-Name`}>{fmtValue(kev.name)}</Fact>
            <Fact label={t`KEV-Beschreibung`}>{fmtValue(kev.beschreibung)}</Fact>
            <Fact label={t`Im KEV seit`}>{fmtDay(kev.aufgenommen_am)}</Fact>
            <Fact label={t`KEV-Quellen`}>
              {kev.sources.filter(Boolean).join(', ') || DASH}
            </Fact>
            <Fact label={t`Ransomware-Einsatz`}>
              {kev.ransomware === 'Known' ? (
                <Chip tone="alert"><Trans>bekannt</Trans></Chip>
              ) : kev.ransomware === 'Not known' ? (
                <span className="muted"><Trans>nicht bekannt</Trans></span>
              ) : (
                DASH
              )}
            </Fact>
            <Fact label={t`Empfohlene Maßnahme`}>{fmtValue(kev.empfohlene_massnahme)}</Fact>
          </>
        ) : (
          <Fact label="KEV">
            <span className="muted">
              <Trans>
                Keine KEV-Details vorhanden: keine KEV-Assertion oder Abruf aus Dependency-Track
                fehlgeschlagen.
              </Trans>
            </span>
          </Fact>
        )}
      </dl>
    </div>
  );
}

/** The affected KomponenteVersion and the ProduktVersionen that contain it. */
export function EventScopeFacts({ event }: EventFactsProps): ReactNode {
  const { t } = useLingui();
  const kunden = kundenOf(event);
  const manufacturer = event.komponente.hersteller;
  return (
    <div className="triage-section">
      <h3><Trans>Betroffener Umfang</Trans></h3>
      <dl className="detail-list">
        <Fact label={t`Komponentenversion`}>
          {fmtValue(event.komponente.name)}
          {event.komponente.version ? (
            <span className="muted"> {event.komponente.version}</span>
          ) : null}
          {manufacturer ? (
            <div className="prod"><Trans>Hersteller: {manufacturer}</Trans></div>
          ) : null}
        </Fact>
        <Fact label={t`Enthalten in Produktversionen`}>
          {event.produktversionen.length === 0 ? (
            <span className="muted"><Trans>In keiner Produktversion, nur im Katalog.</Trans></span>
          ) : (
            <ChipList>
              {event.produktversionen.map((version) => (
                <Chip key={version.id ?? produktVersionLabel(version)}>
                  {produktVersionLabel(version)}
                </Chip>
              ))}
            </ChipList>
          )}
        </Fact>
        <Fact label={t`Betroffene Kunden`}>
          {kunden.length === 0 ? (
            <span className="muted"><Trans>Keine, es ist keine Produktinstanz ausgeliefert.</Trans></span>
          ) : (
            <ChipList>
              {kunden.map((kunde) => (
                <Chip key={kunde.id ?? kunde.name ?? '?'} title={kunde.email ?? t`Keine E-Mail hinterlegt`}>
                  {fmtValue(kunde.name)}
                </Chip>
              ))}
            </ChipList>
          )}
        </Fact>
        <Fact label={t`Betroffene Produktinstanzen`}>
          {event.betroffene.length === 0 ? (
            <span className="muted"><Trans>Keine</Trans></span>
          ) : (
            `${event.betroffene.length}`
          )}
        </Fact>
      </dl>
    </div>
  );
}

/** Verdict, who recorded it, when, why — plus the notification counters. */
export function EventAuditTrail({ event }: EventFactsProps): ReactNode {
  const { t } = useLingui();
  const sentCount = event.benachrichtigungen_versendet;
  const openCount = event.benachrichtigungen_offen;
  return (
    <div className="triage-section">
      <h3><Trans>Audit-Trail</Trans></h3>
      <dl className="detail-list">
        <Fact label={t`Bewertung`}>
          <Chip tone={BEWERTUNG_TONE[event.bewertung]}>{BEWERTUNG_LABELS[event.bewertung]}</Chip>
        </Fact>
        <Fact label={t`Bewertet von`}>{fmtValue(event.bewertet_von)}</Fact>
        <Fact label={t`Bewertet am`}>{fmtDate(event.bewertet_am)}</Fact>
        <Fact label={t`Begründung`}>
          {event.begruendung ? (
            <blockquote className="triage-quote">{event.begruendung}</blockquote>
          ) : (
            <span className="muted"><Trans>Keine Begründung erfasst.</Trans></span>
          )}
        </Fact>
        <Fact label={t`Event erstellt am`}>{fmtDate(event.erstellt_am)}</Fact>
        <Fact label={t`Benachrichtigungen`}>
          {sentCount === 0 && openCount === 0 ? (
            <span className="muted"><Trans>Keine Benachrichtigung angelegt.</Trans></span>
          ) : (
            <>
              <Chip tone="good"><Trans>{sentCount} versendet</Trans></Chip>
              {openCount > 0 ? (
                <span className="prod"><Trans>{openCount} ausstehend</Trans></span>
              ) : null}
            </>
          )}
        </Fact>
      </dl>
    </div>
  );
}

/** Per-instance table: who has it, on which version, and its notification state. */
export function EventInstanzen({ event }: EventFactsProps): ReactNode {
  const { t } = useLingui();
  const count = event.betroffene.length;
  const columns: readonly DataTableColumn<BetroffeneInstanz>[] = [
    {
      key: 'serial',
      header: t`Seriennummer`,
      render: (instance) => <span className="mono">{fmtValue(instance.seriennummer)}</span>,
    },
    {
      key: 'product',
      header: t`Produkt`,
      render: (instance) => fmtValue(instance.produkttyp),
    },
    {
      key: 'version',
      header: t`Produktversion`,
      render: (instance) => instance.produktversion ? `V${instance.produktversion}` : DASH,
    },
    {
      key: 'customer',
      header: t`Kunde`,
      render: (instance) => fmtValue(instance.kunde.name),
    },
    {
      key: 'email',
      header: t`Kontakt-E-Mail`,
      render: (instance) => instance.kunde.email || <span className="muted"><Icon name="warning" size={14} /> <Trans>keine Kontakt-E-Mail</Trans></span>,
    },
    {
      key: 'notification',
      header: t`Benachrichtigung`,
      render: (instance) => {
        const notification = instance.benachrichtigung;
        return notification === null ? <span className="muted"><Trans>nicht angelegt</Trans></span> : (
          <>
            <Chip tone={notificationTone(notification.status)}>
              {NOTIFICATION_STATUS_LABELS[notification.status] ?? notification.status}
            </Chip>
            <div className="prod">
              {notification.id === null ? t`ohne ID` : `#${notification.id}`}
              {isNotificationSent(notification.status) ? ` · ${fmtDate(notification.versendet_am)}` : ''}
            </div>
          </>
        );
      },
    },
  ];
  return (
    <div className="triage-section">
      <h3><Trans>Ausgelieferte Produktinstanzen ({count})</Trans></h3>
      {count === 0 ? (
        <EmptyState
          compact
          title={t`Keine Produktinstanz läuft nach bekannter Konfiguration mit dieser Komponentenversion`}
        />
      ) : (
        <div className="triage-instance-table">
          <DataTable
            rows={event.betroffene}
            columns={columns}
            rowKey={(instance) => instance.instanz_id
              ?? `${instance.seriennummer ?? 'sn'}-${event.betroffene.indexOf(instance)}`}
            mobileBreakpoint={760}
            pageSize={10}
            paginationResetKey={String(event.id)}
            resetPageOnRowsChange={false}
            caption={t`Betroffene Produktinstanzen und Benachrichtigungen`}
          />
        </div>
      )}
    </div>
  );
}
